import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, LexicalEditor } from "lexical";
import { $isRootTextContentEmpty } from "@lexical/text";
import { useEffect, useState } from "react";
import { $generateHtmlFromNodes } from "@lexical/html";
import { mergeRegister } from "@lexical/utils";

interface CustomOnTouchPluginProps {
  onTouch?: (value: Record<"json" | "html", string>) => void;
  initialValue?: string;
}

export default function CustomOnTouchPlugin({
  onTouch,
  initialValue,
}: CustomOnTouchPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [previousValue, setPreviousValue] = useState<string>();

  function isEditorEmpty(editor: LexicalEditor) {
    return editor.getEditorState().read(() => {
      const root = $getRoot();
      const isEmpty: boolean =
        $isRootTextContentEmpty(true) && root?.getChildrenSize() === 1;
      return isEmpty;
    });
  }

  function handleTouch(editor: LexicalEditor) {
    if (!editor.isEditable || isEditorEmpty(editor)) {
      return;
    }
    const editorState = editor.getEditorState();
    const json = editorState.toJSON();
    const jsonString = JSON.stringify(json);
    const htmlString = $generateHtmlFromNodes(editor);

    if (
      jsonString === previousValue ||
      jsonString === initialValue ||
      htmlString === initialValue
    ) {
      return;
    }
    onTouch?.({
      json: jsonString,
      html: htmlString,
    });
    setPreviousValue(jsonString);
  }

  useEffect(() => {
    if (!editor) {
      return;
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          handleTouch(editor);
        });
      })
    );
  }, [editor, onTouch, initialValue]);

  return null;
}
